import { useCallback } from 'react';

/**
 * Custom hook to manage array field operations (add, remove, update)
 * @param {Array} items - Current array of items
 * @param {Function} onUpdate - Callback to update the field: (newItems) => void
 * @param {object} defaultItem - Default item structure for new items
 * @returns {object} - { items, addItem, removeItem, updateItem, updateItemField }
 */
const useArrayField = (items = [], onUpdate, defaultItem = {}) => {
  const addItem = useCallback(() => {
    const newItems = [...items, typeof defaultItem === 'function' ? defaultItem() : { ...defaultItem }];
    onUpdate(newItems);
  }, [items, onUpdate, defaultItem]);

  const removeItem = useCallback((index) => {
    const newItems = items.filter((_, i) => i !== index);
    onUpdate(newItems);
  }, [items, onUpdate]);

  const updateItem = useCallback((index, updatedItem) => {
    const newItems = [...items];
    newItems[index] = typeof updatedItem === 'function' 
      ? updatedItem(newItems[index])
      : { ...newItems[index], ...updatedItem };
    onUpdate(newItems);
  }, [items, onUpdate]);

  const updateItemField = useCallback((index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onUpdate(newItems);
  }, [items, onUpdate]);

  return {
    items,
    addItem,
    removeItem,
    updateItem,
    updateItemField,
  };
};

export default useArrayField;
